// Render-time auto-linking. Walks the already-rendered article DOM and turns the
// FIRST mention of each registry target into a link — conservatively:
//   • one link per target (no repeats),
//   • whole-word, case-insensitive matches only,
//   • never inside existing links, headings, code, or buttons,
//   • a hard cap so an article never turns into a sea of blue.
//
// Emits the same data-attributes the article view already wires (data-article-link
// / data-view-link), so clicks route through the SPA with no extra handlers.
const MAX_LINKS = 10;
const SKIP_TAGS = new Set(["A", "CODE", "PRE", "H1", "H2", "H3", "H4", "H5", "H6", "BUTTON", "TABLE"]);

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function linkHTML(entry, text) {
  if (entry.kind === "view") {
    return `<a href="#/${entry.target}" data-view-link="${entry.target}" class="text-sage-700 underline decoration-sage-300 underline-offset-2 hover:decoration-sage-500">${text}</a>`;
  }
  return `<a href="#/article?slug=${entry.target}" data-article-link="${entry.target}" class="text-sage-700 underline decoration-sage-300 underline-offset-2 hover:decoration-sage-500">${text}</a>`;
}

export function applyAutoLinks(container, pairs) {
  if (!container || !pairs || !pairs.length) return;
  const usedTargets = new Set();
  let linkCount = 0;

  for (const { alias, entry } of pairs) {
    if (linkCount >= MAX_LINKS) break;
    if (usedTargets.has(entry.target)) continue;

    const re = new RegExp("\\b" + escapeRegex(alias) + "\\b", "i");

    // Fresh walker per alias: we only need the first eligible text node.
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        let p = node.parentElement;
        while (p && p !== container) {
          if (SKIP_TAGS.has(p.tagName)) return NodeFilter.FILTER_REJECT;
          p = p.parentElement;
        }
        return re.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });

    const node = walker.nextNode();
    if (!node) continue;

    const m = re.exec(node.nodeValue);
    if (!m) continue;
    const idx = m.index;
    const matchText = node.nodeValue.slice(idx, idx + alias.length);

    const before = document.createTextNode(node.nodeValue.slice(0, idx));
    const after = document.createTextNode(node.nodeValue.slice(idx + alias.length));
    const span = document.createElement("span");
    span.innerHTML = linkHTML(entry, matchText);
    const anchor = span.firstChild;

    const parent = node.parentNode;
    parent.insertBefore(before, node);
    parent.insertBefore(anchor, node);
    parent.insertBefore(after, node);
    parent.removeChild(node);

    usedTargets.add(entry.target);
    linkCount++;
  }
}
